import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { marked } from "marked";
import { ArrowLeft, FileDown } from "lucide-react";
import { useSeo } from "@/hooks/useSeo";
import { Eyebrow } from "@/components/Reveal";
import NewsletterForm from "@/components/NewsletterForm";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Article() {
  const { slug } = useParams();
  const [meta, setMeta] = useState(null);
  const [html, setHtml] = useState("");
  const [missing, setMissing] = useState(false);

  useSeo(
    meta ? `${meta.title} | Reform Intelligence®` : "Insights | Reform Intelligence®",
    meta ? meta.summary : "Research and perspective on trustworthy AI from Reform Intelligence."
  );

  useEffect(() => {
    fetch(`${API}/articles/${slug}`)
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((art) => {
        if (art.coming_soon) { setMissing(true); return; }
        setMeta(art);
        setHtml(marked.parse(art.body || ""));
      })
      .catch(() => {
        fetch("/insights/manifest.json")
          .then((r) => r.json())
          .then((posts) => {
            const m = posts.find((p) => p.slug === slug);
            if (!m || m.coming_soon) { setMissing(true); return; }
            setMeta(m);
            return fetch(`/insights/${slug}.md`)
              .then((r) => (r.ok ? r.text() : Promise.reject()))
              .then((text) => setHtml(marked.parse(text)))
              .catch(() => setMissing(true));
          })
          .catch(() => setMissing(true));
      });
  }, [slug]);

  if (missing) {
    return (
      <section className="mx-auto max-w-3xl px-5 py-24 sm:px-8">
        <h1 className="font-serif2 text-3xl font-semibold text-[#1F2D4E]">Article not available.</h1>
        <Link to="/insights" className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-[#B08D3F]">
          <ArrowLeft size={16} /> Back to Insights
        </Link>
      </section>
    );
  }

  return (
    <article data-testid="article-page" className="bg-white">
      <div className="mx-auto max-w-3xl px-5 py-16 sm:px-8 sm:py-24">
        <Link to="/insights" data-testid="article-back" className="inline-flex items-center gap-2 text-sm font-semibold text-[#B08D3F] transition-colors hover:text-[#1F2D4E]">
          <ArrowLeft size={16} /> Back to Insights
        </Link>
        {meta && (
          <header className="mt-10 border-b border-[#E4E7EC] pb-10">
            <Eyebrow>{meta.category}</Eyebrow>
            <h1 className="mt-4 font-serif2 text-3xl font-semibold leading-tight text-[#1F2D4E] sm:text-4xl">{meta.title}</h1>
            <p className="mt-4 text-sm text-[#98A0B3]">
              {meta.author}{meta.date ? ` · ${meta.date}` : ""}
            </p>
            {meta.pdf && (
              <a
                href={meta.pdf.includes("/") ? `${API}/files/${meta.pdf}` : `/insights/${meta.pdf}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="article-pdf"
                className="mt-6 inline-flex items-center gap-2 border border-[#1F2D4E] px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.12em] text-[#1F2D4E] transition-colors hover:border-[#B08D3F] hover:text-[#B08D3F]"
              >
                <FileDown size={15} /> Download PDF
              </a>
            )}
          </header>
        )}
        <div className="prose-ri mt-10" dangerouslySetInnerHTML={{ __html: html }} />
        <div className="mt-16 border border-[#E4E7EC] bg-[#F5F6F8] p-8">
          <Eyebrow>Newsletter</Eyebrow>
          <p className="mt-3 font-serif2 text-xl font-semibold text-[#1F2D4E]">Get our research. Enter your email.</p>
          <div className="mt-5">
            <NewsletterForm testid="article-newsletter" />
          </div>
        </div>
      </div>
    </article>
  );
}
